import { withDashboardLayout } from 'layouts/DashboardLayout/DashboardLayout';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAlert } from 'context/AlertProvider';
import PageContainer from 'components/PageContainer/PageContainer';
import { Card, Dimmer, Loader, Message } from 'semantic-ui-react';
import { getMessageFromAxiosError } from 'modules/message-factory/message-factory';
import { ITEMS_PER_PAGE } from 'constants/constants';
import PendingManifestKPI from 'components/PendingManifestKPI/PendingManifestKPI';
import { useUnmanifestedShipments } from 'hooks/useUnmanifestedShipments';
import { getAll } from 'services/manifests.service';
import ManifestsTable from 'components/ManifestsTable/ManifestsTable';
import ManifestsFiltersBar from 'components/ManifestsFiltersBar/ManifestsFiltersBar';
import useFiltersReducer, {
  loadMoreAction,
  updateFiltersAction,
} from 'hooks/useFiltersReducer';
import { useLocalFilteredResults } from 'hooks/useLocalFilteredResults';

function ManifestsPage() {
  const alert = useAlert();
  const [manifests, setManifests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [query, setQuery] = useState('');
  const [hasNextPage, setHasNextPage] = useState(true);
  const [state, dispatch] = useFiltersReducer({
    agency_id: [],
    page: 1,
  });
  const { unmanifestedShipments, fetchUnmanifestedShipments } =
    useUnmanifestedShipments();
  const isFirstLoad = useRef(true);

  const fetchManifests = useCallback(
    async (state) => {
      try {
        setLoading(true);
        const { data: manifests } = await getAll({
          agency_id: state.agency_id,
          page: state.page,
          itemsPerPage: ITEMS_PER_PAGE,
        });
        setHasNextPage(manifests?.length === ITEMS_PER_PAGE);
        setManifests((prev) =>
          state.page > 1 ? [...prev, ...manifests] : manifests,
        );
      } catch (error) {
        alert.current.error(getMessageFromAxiosError(error));
      } finally {
        setLoading(false);
      }
    },
    [alert],
  );

  const filteredManifests = useLocalFilteredResults(manifests, query);

  const updateFilters = useCallback(
    (filters) => {
      if (isFirstLoad.current) {
        isFirstLoad.current = false;
      }
      dispatch(updateFiltersAction(filters));
    },
    [dispatch],
  );

  useEffect(() => {
    (async () => {
      if (isFirstLoad.current) return;
      await fetchManifests(state);
    })();
  }, [state, fetchManifests]);

  return (
    <PageContainer title="Manifiestos" className="manifests-page">
      <Dimmer active={loading && state.page === 1} inverted>
        <Loader>Recuperando manifiestos…</Loader>
      </Dimmer>
      <Dimmer active={printing} inverted>
        <Loader>Imprimiendo manifiesto y etiqueta de pallet…</Loader>
      </Dimmer>
      <>
        <Card.Header>
          <ManifestsFiltersBar
            onFiltersChange={updateFilters}
            query={query}
            onChangeQuery={setQuery}
          />
        </Card.Header>
        <Card.Content>
          {!!filteredManifests.length ? (
            <>
              <ManifestsTable
                loading={loading}
                manifests={filteredManifests}
                setPrinting={setPrinting}
                onLoadMoreItems={(skip) => dispatch(loadMoreAction())}
                hasNextPage={hasNextPage}
              />
              <small>{manifests.length} items</small>
            </>
          ) : (
            <Message info>
              No hay manifiestos que mostrar para los criterios de filtrado
              seleccionado…
            </Message>
          )}
        </Card.Content>
        <PendingManifestKPI
          unmanifestedShipments={unmanifestedShipments}
          fetchUnmanifestedShipments={fetchUnmanifestedShipments}
          onManifestDownloaded={() => fetchManifests(state)}
        />
      </>
    </PageContainer>
  );
}

export default withDashboardLayout(ManifestsPage);
